import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { CompanyRole, CompanyRoleScope, PermissionFlag, PermissionSet } from '@/lib/api/company/roles'
import {
  faArrowDownToLineRegular,
  faCalendarRegular,
  faEarthEurope,
  faGearRegular,
  faReceiptRegular,
  faSteeringWheelRegular,
  faTruckRegular,
  faUsersRegular,
} from '@/lib/fa'

type Translate = (key: string, data?: Record<string, unknown>) => string

const k = (key: string) => `usersModule.rolesTab.${key}`

// Ikony jak w lewym pasku, żeby grupę łatwo skojarzyć z modułem.
export const PERMISSION_GROUPS: { key: string; icon: IconDefinition; flags: PermissionFlag[] }[] = [
  { key: 'activities', icon: faSteeringWheelRegular, flags: ['canViewActivities', 'canEditActivities', 'canEditActivityDay', 'canEditGpsData', 'canGenerateAttestation'] },
  { key: 'companyUsers', icon: faUsersRegular, flags: ['canCreateUser', 'canEditUser', 'canRemoveUser', 'canCreateCompany', 'canEditCompany', 'canRemoveCompany'] },
  { key: 'delegations', icon: faEarthEurope, flags: ['canViewDelegations', 'canEditDelegations'] },
  { key: 'payroll', icon: faReceiptRegular, flags: ['canViewBillingData', 'canEditBillingData'] },
  { key: 'files', icon: faArrowDownToLineRegular, flags: ['canImportFiles', 'canExportData', 'canGeneratePrintouts'] },
  { key: 'alerts', icon: faCalendarRegular, flags: ['canViewAlerts', 'canEditAlerts'] },
  { key: 'settings', icon: faGearRegular, flags: ['canViewConfiguration', 'canReadDatabaseLogin'] },
  { key: 'drivers', icon: faTruckRegular, flags: ['canAddDriver'] },
]

// Serwer przepuszcza podgląd z samym prawem edycji, więc edycja zawsze zawiera podgląd.
export const IMPLIED_BY: Partial<Record<PermissionFlag, PermissionFlag>> = {
  canViewActivities: 'canEditActivities',
  canViewDelegations: 'canEditDelegations',
  canViewBillingData: 'canEditBillingData',
  canViewAlerts: 'canEditAlerts',
}

export const impliedBy = (set: PermissionSet, flag: PermissionFlag): PermissionFlag | null => {
  const source = IMPLIED_BY[flag]
  return source && set[source] ? source : null
}

export const has = (set: PermissionSet, flag: PermissionFlag) => set[flag] || !!impliedBy(set, flag)

export const withImplied = (set: PermissionSet): PermissionSet => {
  const result = { ...set }
  for (const [view, edit] of Object.entries(IMPLIED_BY) as [PermissionFlag, PermissionFlag][]) {
    if (result[edit]) result[view] = true
  }
  return result
}

export const countPermissions = (set: PermissionSet) =>
  PERMISSION_GROUPS.flatMap((g) => g.flags).filter((f) => has(set, f)).length

export const TOTAL_PERMISSIONS = PERMISSION_GROUPS.reduce((n, g) => n + g.flags.length, 0)

export const permissionLabel = (t: Translate, flag: PermissionFlag) => t(k(`permissions.${flag}.label`))
export const permissionDescription = (t: Translate, flag: PermissionFlag) => t(k(`permissions.${flag}.description`))
export const groupLabel = (t: Translate, group: string) => t(k(`groups.${group}`))

export const isOwnerRole = (role: CompanyRole) => role.systemRole && role.name === 'Company Owner'
export const isDriverRole = (role: CompanyRole) => role.isDriver

/** Opis dla ludzi: role systemowe mają tłumaczenie, reszta własny opis. */
export const roleDescription = (t: Translate, role: CompanyRole): string | null => {
  if (isOwnerRole(role)) return t(k('systemDescription.owner'))
  if (isDriverRole(role)) return t(k('driverRole'))
  return role.description
}

export const scopeLabel = (t: Translate, scope: CompanyRoleScope) => t(k(`scope.${scope}`))

export const scopeHint = (t: Translate, role: CompanyRole, companyIsOwner: boolean) => {
  if (role.scope === 'serviceAccount') {
    return companyIsOwner
      ? t(k('scopeHint.serviceAccountOwn'))
      : t(k('scopeHint.serviceAccount'), { name: role.ownerCompanyName ?? '' })
  }
  if (role.scope === 'parent') return t(k('scopeHint.parent'), { name: role.ownerCompanyName ?? '' })
  return t(k(`scopeHint.${role.scope}`))
}

/** Dlaczego rola jest tylko do odczytu (null = można ją edytować). */
export const lockReason = (t: Translate, role: CompanyRole, companyId: string, canManage: boolean): string | null => {
  if (role.canEdit) return null
  if (role.ownerCompanyId === companyId && !canManage) return t(k('readOnly'))
  return scopeHint(t, role, role.ownerCompanyId === companyId)
}

export const SCOPE_BADGE: Record<CompanyRoleScope, string> = {
  system: 'tw-bg-primary tw-text-content-primary',
  serviceAccount: 'tw-bg-secondary tw-text-primary',
  parent: 'tw-bg-secondary tw-text-primary',
  company: 'tw-border tw-border-primary tw-text-primary',
  shared: 'tw-border tw-border-base-300 tw-text-content-base',
}

export const roleErrorMessage = (t: Translate, code: string, message?: string) => {
  const known = ['ROLE_EXISTS', 'NAME_REQUIRED', 'INVALID_INPUT', 'USER_CANNOT_ALTER_ROLE', 'ROLE_IN_USE', 'ROLE_NOT_EDITABLE', 'NOT_AUTHORIZED']
  return known.includes(code) ? t(k(`errors.${code}`), { users: message ?? '' }) : t(k('errors.default'))
}

export const rolesKey = k

/** Rola własna obowiązuje też w firmach podrzędnych - wtedy liczymy osoby we wszystkich. */
export const roleUserCount = (role: CompanyRole) => Math.max(role.usersInCompany, role.usersAffected ?? 0)

export const roleUsersText = (t: Translate, role: CompanyRole) => {
  if (role.scope === 'serviceAccount' && role.usersAffected != null)
    return t(k('usersInAccount'), { count: roleUserCount(role) })
  if ((role.usersAffected ?? 0) > role.usersInCompany)
    return t(k('usersWithChildren'), { count: roleUserCount(role) })
  return t(k('usersInCompany'), { count: role.usersInCompany })
}
